import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { photoSrc } from "@/lib/api";

export function PhotoLightbox({ photos, index, onClose, onIndexChange }) {
  const [zoom, setZoom] = useState(false);

  useEffect(() => setZoom(false), [index]);

  useEffect(() => {
    if (index == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndexChange((index + 1) % photos.length);
      if (e.key === "ArrowLeft") onIndexChange((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, onClose, onIndexChange]);

  if (index == null) return null;
  const p = photos[index];
  if (!p) return null;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/90 backdrop-blur-sm" data-testid="photo-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full capitalize font-medium ${p.kind === "issue" ? "bg-destructive text-white" : "bg-emerald-600 text-white"}`}>{p.kind}</span>
          <span className="text-sm text-white/70 font-mono">{index + 1} / {photos.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom((z) => !z)} className="rounded-full p-2 text-white/90 hover:bg-white/10 transition-colors" data-testid="lightbox-zoom" aria-label="Toggle zoom">
            {zoom ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
          </button>
          <button onClick={onClose} className="rounded-full p-2 text-white/90 hover:bg-white/10 transition-colors" data-testid="lightbox-close" aria-label="Close">
            <X size={22} />
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 && (
          <button onClick={() => onIndexChange((index - 1 + photos.length) % photos.length)} className="absolute left-4 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors" data-testid="lightbox-prev" aria-label="Previous">
            <ChevronLeft size={24} />
          </button>
        )}
        <img
          src={photoSrc(p.image_base64)}
          alt={p.note || p.kind}
          onClick={() => setZoom((z) => !z)}
          className={`select-none object-contain transition-transform duration-200 ${zoom ? "max-h-none max-w-none scale-150 cursor-zoom-out" : "max-h-full max-w-full cursor-zoom-in"}`}
        />
        {photos.length > 1 && (
          <button onClick={() => onIndexChange((index + 1) % photos.length)} className="absolute right-4 z-10 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors" data-testid="lightbox-next" aria-label="Next">
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {p.note && <div className="px-4 py-3 text-center text-sm text-white/80" onClick={(e) => e.stopPropagation()}>{p.note}</div>}
    </div>
  );
}
